import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { formatEuroCents } from '../lib/format';
import { CardTerminalIcon } from '../admin/NavIcons';
import KartenStatus from './KartenStatus';
import { eventGeschlossen, useKartenzahlung } from './useKartenzahlung';

/**
 * 2026-10-15 (v0.17 Stufe 3, FR-262–FR-264): „Mit Karte bezahlen" für eine Person an einem
 * gewöhnlichen Event.
 *
 * Kassiert wird **immer der ganze offene Betrag** (D-9) -- nichts wird getippt. Der Dialog zeigt
 * ihn noch einmal mit dem Namen, bevor er ans Terminal geht: Wer vorher sieht, wessen Saldo er
 * kassiert, tippt nicht beim falschen Namen. Hat sich der Betrag inzwischen geändert (eine Buchung
 * kam dazu, eine wurde zurückgenommen), startet der Server nichts (412); der Dialog lädt den neuen
 * Betrag und lässt neu bestätigen.
 *
 * Kein Pfand -- das gibt es nur am Sammelgast-Event (D-11, AC-251).
 */
export default function PersonKartenDialog({
  memberId,
  name,
  balanceCents,
  terminalName,
  onClose,
  onPaid,
  onEventClosed,
  onDeviceInvalid,
}: {
  memberId: string;
  name: string;
  /** Der offene Saldo, wie der Kiosk ihn gerade zeigt. */
  balanceCents: number;
  terminalName?: string;
  onClose: () => void;
  /** Nach „Zahlung erhalten", mit dem neuen offenen Saldo. */
  onPaid: (balanceCents: number) => void;
  onEventClosed: () => void;
  onDeviceInvalid: () => void;
}) {
  const [offen, setOffen] = useState(balanceCents);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const backButtonRef = useRef<HTMLButtonElement>(null);

  // Über eine Ref -- der Kiosk erzeugt die Rückrufe bei jedem Zeichnen neu (Lehre vom 15.10.).
  const rueckrufe = useRef({ onPaid, onEventClosed, onDeviceInvalid });
  useEffect(() => {
    rueckrufe.current = { onPaid, onEventClosed, onDeviceInvalid };
  });

  const handleAuthErrors = useCallback((e: unknown): boolean => {
    if (eventGeschlossen(e)) {
      rueckrufe.current.onEventClosed();
      return true;
    }
    // Eine fachliche 403 („am Kiosk ausgeblendet") fängt schon `useKartenzahlung.starte` ab; hier
    // landen nur noch die des Autorisierers.
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      rueckrufe.current.onDeviceInvalid();
      return true;
    }
    return false;
  }, []);

  const karte = useKartenzahlung({
    onAuthError: handleAuthErrors,
    onErfolg: (b) => rueckrufe.current.onPaid(b),
  });
  const { phase } = karte;

  // Fokus auf „Zurück": ein versehentliches Enter soll nichts ans Terminal schicken.
  useEffect(() => {
    backButtonRef.current?.focus();
  }, []);

  const neuLaden = async () => {
    try {
      const r = await api.get<{ totalCents?: number }>(
        `/kiosk/members/${encodeURIComponent(memberId)}/balance`,
        'kiosk',
      );
      setOffen(r.totalCents ?? 0);
    } catch (e) {
      if (!handleAuthErrors(e)) setError('Der offene Betrag konnte nicht geladen werden.');
    }
  };

  const mitKarte = async () => {
    setSubmitting(true);
    setError(undefined);
    setNotice(undefined);
    try {
      const ergebnis = await karte.starte({ memberId, totalCents: offen });
      if (ergebnis.art === 'geaendert') {
        setNotice('Der offene Betrag hat sich geändert. Bitte den neuen Betrag prüfen.');
        await neuLaden();
      } else if (ergebnis.art === 'fehler') {
        setError(ergebnis.text);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const schliessbar =
    phase.art === 'bereit' || phase.art === 'gescheitert' || phase.art === 'ohneZuordnung';

  return (
    <div
      className="confirm-overlay"
      role="presentation"
      onClick={() => {
        if (schliessbar) onClose();
      }}
    >
      <div
        className="confirm-dialog kiosk-settle-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Mit Karte bezahlen"
        onClick={(e) => e.stopPropagation()}
      >
        <h2>Mit Karte bezahlen</h2>
        {notice && <div className="kiosk-settle-notice">{notice}</div>}
        {error && <div className="error-banner">{error}</div>}

        <KartenStatus
          phase={phase}
          terminalName={terminalName}
          backButtonRef={backButtonRef}
          onAbbrechen={(z) => void karte.abbrechen(z)}
          onErneutNachfragen={karte.erneutNachfragen}
          onKeineAntwortAbbrechen={() => {
            karte.zurueck();
            void neuLaden();
          }}
          onZurueck={onClose}
        />

        {(phase.art === 'bereit' || phase.art === 'gescheitert') && (
          <>
            {phase.art === 'gescheitert' && <div className="error-banner">{phase.grund}</div>}
            <div className="kiosk-card-status">
              <p>{name}</p>
              <p className="kiosk-card-amount">Offen: {formatEuroCents(offen)}</p>
              <p>Kassiert wird der ganze offene Betrag.</p>
            </div>
            <div className="confirm-dialog-actions">
              <button ref={backButtonRef} onClick={onClose}>
                Zurück
              </button>
              <button
                className="primary-button"
                disabled={submitting || offen <= 0}
                onClick={() => void mitKarte()}
              >
                <CardTerminalIcon />
                {phase.art === 'gescheitert' ? 'Erneut mit Karte' : 'Mit Karte'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
